import { Link } from "react-router-dom";
import { Heart, Sunrise, Home as HomeIcon, Sparkles, GraduationCap, Stethoscope } from "lucide-react";
import Marquee from "@/components/Marquee";
import { Reveal, MaskedLines } from "@/components/Reveal";
import { ZoomableImage } from "@/components/Lightbox";
import { Pillars } from "@/components/Pillars";

const values = [
  { icon: Heart, title: "Dignity First", text: "Every resident is someone's child. We care for them the way Jaliyah cared for everyone she loved." },
  { icon: Sunrise, title: "Hope Is a Practice", text: "Structure, routine, and 24/7 support turn fragile beginnings into strong tomorrows." },
  { icon: HomeIcon, title: "A Real Home", text: "Not a facility — a residence. Warm, safe, and built for healing in community." },
  { icon: Sparkles, title: "Transformation", text: "The butterfly in our name is a promise: with the right space, people change." },
];

const gallery = [
  { src: "/images/jaliyah-tote.webp", alt: "Jaliyah with her favorite tote bag" },
  { src: "/images/jaliyah-red-dress.jpg", alt: "Jaliyah dressed up in red, smiling" },
  { src: "/images/jaliyah-portrait.jpg", alt: "Close-up portrait of Jaliyah" },
];

export default function Story() {
  return (
    <div data-testid="story-page">
      <section className="relative overflow-hidden bg-[#FAF8F5] grain">
        <div className="absolute -top-20 -right-20 w-96 h-96 rounded-full bg-[#FEF3C7] blur-3xl opacity-80" />
        <div className="max-w-5xl mx-auto px-5 sm:px-8 pt-16 pb-20 lg:pt-24 text-center relative">
          <p className="text-xs uppercase tracking-[0.25em] font-bold text-[#B45309] mb-4">In Loving Memory</p>
          <MaskedLines
            className="font-display font-black text-[#2E1065] text-4xl sm:text-5xl lg:text-6xl leading-[1.08] tracking-tight"
            lines={["Jaliyah Ikja Mason", "One of God's sweetest angels"]}
          />
          <Reveal delay={0.4}>
            <p className="mt-6 font-display italic text-xl sm:text-2xl text-[#5B21B6]" data-testid="memorial-dates">
              June 23, 2010 — May 20, 2026
            </p>
            <p className="mt-6 text-base sm:text-lg text-slate-700 leading-relaxed max-w-3xl mx-auto" data-testid="story-intro">
              Born to Shereice Walker and Davonte Mason, Jaliyah was called back home to be with the Lord after
              living her life to the fullest for a fabulous 15 years. This house — every house under this
              umbrella — exists so her name keeps doing what she always did: lighting up rooms and lifting
              people up.
            </p>
            <p className="mt-4 text-xs uppercase tracking-wider font-bold text-slate-400" data-testid="story-photo-hint">
              Tap any photo to view it full size
            </p>
          </Reveal>
        </div>
      </section>

      <Marquee />

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20 lg:py-28 grid lg:grid-cols-2 gap-14 items-center">
        <Reveal>
          <div className="relative">
            <div className="absolute -inset-3 bg-gradient-to-br from-[#5B21B6] to-[#F59E0B] rounded-[2rem] rotate-1 opacity-80" />
            <ZoomableImage
              src="/images/jaliyah-blonde.jpg"
              alt="Jaliyah Ikja Mason"
              className="relative rounded-[2rem] shadow-2xl"
              imgClassName="w-full h-[440px] sm:h-[520px] object-cover object-top"
              testId="story-portrait"
            />
            <div className="absolute -bottom-5 -right-3 sm:-right-5 bg-white rounded-2xl shadow-xl px-5 py-4 border border-purple-100">
              <p className="font-display font-bold text-[#2E1065]">A fabulous 15 years</p>
              <p className="text-xs text-slate-500">Forever our butterfly</p>
            </div>
          </div>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="text-xs uppercase tracking-[0.25em] font-bold text-[#B45309] mb-3">Her Life</p>
          <h2 className="font-display font-bold text-3xl sm:text-4xl text-[#1E1B4B] leading-tight" data-testid="story-life-heading">
            Big dreams, and the heart to match them
          </h2>
          <div className="mt-6 space-y-4 text-base text-slate-700 leading-relaxed">
            <p>
              Jaliyah was baptized at an early age at God's Mission of Faith. A rising 10th grader at
              Dinwiddie High School, she was already seeking ways to accelerate her education so she could
              become a NICU nurse by age 21 — Jaliyah had really big dreams, and she knew she could
              accomplish them.
            </p>
            <p>
              She was a devoted employee at Croaker's Spot. She loved the finer things in life — and spending
              your money and hers. She treasured her family, friends, and siblings with all her heart, loved
              wholeheartedly, and embraced everyone who showed her love in return.
            </p>
            <p className="font-semibold text-[#5B21B6]">
              No matter where she went, her courageous smile and warm presence would light up any room she
              walked in.
            </p>
          </div>
          <div className="mt-7 flex flex-wrap gap-3">
            <span className="inline-flex items-center gap-2 bg-white border border-purple-100 rounded-full px-4 py-2 text-xs font-bold text-[#2E1065] shadow-sm">
              <GraduationCap size={14} className="text-[#F59E0B]" /> Dinwiddie High School, Class ahead of her dreams
            </span>
            <span className="inline-flex items-center gap-2 bg-white border border-purple-100 rounded-full px-4 py-2 text-xs font-bold text-[#2E1065] shadow-sm">
              <Stethoscope size={14} className="text-[#F59E0B]" /> Future NICU Nurse
            </span>
          </div>
        </Reveal>
      </section>

      <section className="bg-[#2E1065] py-20 lg:py-24 relative grain" data-testid="story-gallery-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal className="text-center mb-12">
            <p className="text-xs uppercase tracking-[0.25em] font-bold text-amber-400 mb-3">Her Light Lives Here</p>
            <h2 className="font-display font-bold text-3xl sm:text-4xl text-[#FAF8F5]" data-testid="gallery-heading">
              The smile that started it all
            </h2>
          </Reveal>
          <div className="grid sm:grid-cols-3 gap-6">
            {gallery.map((g, i) => (
              <Reveal key={g.src} delay={i * 0.12}>
                <ZoomableImage
                  src={g.src}
                  alt={g.alt}
                  className={`rounded-[1.75rem] shadow-2xl border-4 border-amber-400/50 ${i === 1 ? "sm:-translate-y-6" : ""}`}
                  imgClassName="w-full h-80 sm:h-96 object-cover object-top hover:scale-105 transition-transform duration-700"
                  testId={`story-gallery-${i}`}
                />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-5 sm:px-8 py-20 lg:py-28 text-center" data-testid="founder-promise-section">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.25em] font-bold text-[#B45309] mb-3">A Father's Promise</p>
          <h2 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl text-[#1E1B4B] leading-tight" data-testid="story-founder-heading">
            "Her name will keep doing what she always did — lighting up rooms and lifting people up."
          </h2>
          <div className="mt-8 space-y-4 text-base sm:text-lg text-slate-700 leading-relaxed max-w-3xl mx-auto">
            <p>
              Jaliyah's father, co-owner Davonte Mason, founded Jaliyah's House of Hope LLC so his daughter's
              name would live on and keep making the positive impact she made every single day — the impact
              she would have kept making for decades. Every man and woman who finds safety, structure, and a
              fresh start inside these walls becomes part of her legacy.
            </p>
            <p>
              The butterfly in our logo is hers — a symbol that transformation is real, and that with the
              right environment, people do not just recover. They outgrow what held them down, and they fly.
            </p>
          </div>
        </Reveal>
      </section>

      <Pillars dark />

      <section className="bg-[#F4EFE6] py-20 lg:py-28" data-testid="values-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal className="text-center max-w-2xl mx-auto">
            <p className="text-xs uppercase tracking-[0.25em] font-bold text-[#B45309] mb-3">What We Stand For</p>
            <h2 className="font-display font-bold text-3xl sm:text-4xl text-[#1E1B4B]" data-testid="values-heading">
              The values inside every wall we build
            </h2>
          </Reveal>
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.1}>
                <div className="bg-white rounded-3xl p-7 h-full border border-purple-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-500" data-testid={`value-card-${i}`}>
                  <div className="w-12 h-12 rounded-2xl bg-[#F3E8FF] flex items-center justify-center mb-5">
                    <v.icon size={22} className="text-[#5B21B6]" />
                  </div>
                  <h3 className="font-display font-bold text-xl text-[#2E1065]">{v.title}</h3>
                  <p className="mt-3 text-sm text-slate-600 leading-relaxed">{v.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal className="text-center mt-14">
            <Link
              to="/homes"
              data-testid="story-homes-cta"
              className="inline-flex items-center gap-2 bg-[#5B21B6] hover:bg-[#2E1065] text-white font-bold px-8 py-3.5 rounded-full shadow-lg shadow-purple-500/25 transition-colors duration-300"
            >
              See the homes her name built
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
